import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, Play, Plus, Trash2, XCircle } from "lucide-react";
import { api, dt, errMsg, waLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["putus", "lambat", "redaman", "perangkat", "lainnya"];
const PRIORITIES = ["rendah", "normal", "tinggi", "darurat"];
const COLUMNS = [
  ["buka", "Buka", "from-amber-500/15 to-amber-500/5 border-amber-500/30"],
  ["proses", "Proses", "from-sky-500/15 to-sky-500/5 border-sky-500/30"],
  ["selesai", "Selesai", "from-emerald-500/15 to-emerald-500/5 border-emerald-500/30"],
];
const PRIO_CLS = {
  darurat: "border-red-500/40 text-red-400",
  tinggi: "border-orange-500/40 text-orange-400",
  normal: "border-sky-500/40 text-sky-400",
  rendah: "border-border text-muted-foreground",
};

const Field = ({ label, ...props }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    <Input className="h-11 rounded-xl bg-secondary/50" {...props} />
  </div>
);

const EMPTY = { customer_id: "", customer_name: "", phone: "", category: "putus", priority: "normal", description: "", assigned_to: "" };

export default function Tickets() {
  const { user } = useAuth();
  const [tab, setTab] = useState("board");
  const [rows, setRows] = useState([]);
  const [teknisi, setTeknisi] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fTek, setFTek] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [t, tk, c] = await Promise.all([
        api.get(`/tickets?status=${fStatus}&assigned_to=${fTek}`),
        api.get("/tickets/teknisi"),
        api.get("/ftth/customers"),
      ]);
      setRows(t.data); setTeknisi(tk.data); setCustomers(c.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [fStatus, fTek]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/tickets", form);
      toast.success(`Tiket ${data.code} dibuat`); setForm(EMPTY); setTab("board"); load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const setStatus = async (t, status) => {
    try {
      const { data } = await api.put(`/tickets/${t.id}/status`, { status, note: "" });
      toast.success(`Tiket ${t.code} → ${status}`);
      if (detail?.id === t.id) setDetail(data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const assign = async (t, assigned_to) => {
    try {
      const { data } = await api.put(`/tickets/${t.id}/assign`, { assigned_to });
      toast.success("Penugasan diperbarui");
      if (detail?.id === t.id) setDetail(data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/tickets/${detail.id}/notes`, { note });
      setDetail(data); setNote(""); toast.success("Catatan ditambahkan"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Hapus tiket ${t.code}?`)) return;
    try { await api.delete(`/tickets/${t.id}`); toast.success("Tiket dihapus"); setDetail(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const Card = ({ t }) => (
    <div
      className="cursor-pointer rounded-xl border border-border bg-card/80 p-3 transition-colors hover:border-primary/50"
      onClick={() => setDetail(t)} data-testid={`ticket-card-${t.code}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] text-muted-foreground">{t.code}</span>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] ${PRIO_CLS[t.priority]}`}>{t.priority}</span>
      </div>
      <p className="mt-1 truncate text-sm font-medium">{t.customer_name}</p>
      {t.description && <p className="line-clamp-2 text-xs text-muted-foreground">{t.description}</p>}
      <p className="truncate text-xs text-muted-foreground">{t.category} · {t.assigned_name || "belum ditugaskan"}</p>
      <div className="mt-2 flex gap-1">
        {t.status !== "proses" && t.status !== "selesai" && (
          <Button size="sm" variant="outline" className="h-7 rounded-full px-2 text-[11px]" data-testid={`ticket-proses-${t.code}`}
            onClick={(e) => { e.stopPropagation(); setStatus(t, "proses"); }}>
            <Play className="mr-1 h-3 w-3" /> Proses
          </Button>
        )}
        {t.status !== "selesai" && (
          <Button size="sm" variant="outline" className="h-7 rounded-full px-2 text-[11px] text-emerald-400" data-testid={`ticket-selesai-${t.code}`}
            onClick={(e) => { e.stopPropagation(); setStatus(t, "selesai"); }}>
            <CheckCircle2 className="mr-1 h-3 w-3" /> Selesai
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Tabs value={tab} onValueChange={setTab} className="fade-up space-y-6">
      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1">
        {[["board", "Papan Tiket"], ["list", "Daftar"], ["new", "Tiket Baru"]].map(([v, l]) => (
          <TabsTrigger key={v} value={v} data-testid={`tab-ticket-${v}`} className="rounded-lg px-4 py-2 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-sky-500/20 data-[state=active]:to-violet-600/20">{l}</TabsTrigger>
        ))}
      </TabsList>

      <div className="flex flex-wrap items-center gap-2">
        <select className="h-10 rounded-full border border-input bg-secondary/50 px-3 text-sm" data-testid="ticket-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
          <option value="">— semua status —</option>
          {["buka", "proses", "selesai", "batal"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="h-10 rounded-full border border-input bg-secondary/50 px-3 text-sm" data-testid="ticket-filter-teknisi" value={fTek} onChange={(e) => setFTek(e.target.value)}>
          <option value="">— semua teknisi —</option>
          {teknisi.map((t) => <option key={t.id} value={t.id}>{t.username}</option>)}
        </select>
        <p className="text-xs text-muted-foreground" data-testid="ticket-count">{rows.length} tiket{loading ? " · memuat…" : ""}</p>
      </div>

      <TabsContent value="board">
        <div className="grid gap-4 lg:grid-cols-3">
          {COLUMNS.map(([key, label, cls]) => (
            <div key={key} className={`rounded-2xl border bg-gradient-to-b p-4 ${cls}`} data-testid={`ticket-col-${key}`}>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-display text-base font-semibold">{label}</h3>
                <span className="rounded-full bg-background/60 px-2 py-0.5 text-xs">{rows.filter((r) => r.status === key).length}</span>
              </div>
              <div className="space-y-2">
                {rows.filter((r) => r.status === key).map((t) => <Card key={t.id} t={t} />)}
                {!rows.filter((r) => r.status === key).length && <p className="py-6 text-center text-xs text-muted-foreground">Tidak ada tiket</p>}
              </div>
            </div>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="list">
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="ticket-table">
            <TableHeader><TableRow>
              <TableHead>Kode</TableHead><TableHead>Pelanggan</TableHead><TableHead>Kategori</TableHead>
              <TableHead>Prioritas</TableHead><TableHead>Status</TableHead><TableHead>Teknisi</TableHead>
              <TableHead>Dibuat</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id} className="text-xs">
                  <TableCell data-label="Kode" className="font-mono">{t.code}</TableCell>
                  <TableCell data-label="Pelanggan" className="font-medium">{t.customer_name}</TableCell>
                  <TableCell data-label="Kategori">{t.category}</TableCell>
                  <TableCell data-label="Prioritas"><span className={`rounded-full border px-2 py-0.5 text-[10px] ${PRIO_CLS[t.priority]}`}>{t.priority}</span></TableCell>
                  <TableCell data-label="Status">{t.status}</TableCell>
                  <TableCell data-label="Teknisi">{t.assigned_name || "-"}</TableCell>
                  <TableCell data-label="Dibuat">{dt(t.created_at)}</TableCell>
                  <TableCell data-label="" className="space-x-1 text-right cell-actions">
                    <Button size="sm" variant="outline" className="h-7 rounded-full px-2 text-[11px]" onClick={() => setDetail(t)} data-testid={`ticket-open-${t.code}`}>Detail</Button>
                    {t.phone && (
                      <Button asChild size="sm" variant="outline" className="h-7 rounded-full px-2 text-[11px]">
                        <a href={waLink(t.phone, `Tiket ${t.code}: laporan gangguan ${t.category} sedang kami tangani.`)} target="_blank" rel="noreferrer"><MessageCircle className="h-3 w-3" /></a>
                      </Button>
                    )}
                    {user?.role !== "teknisi" && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => remove(t)} data-testid={`ticket-delete-${t.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground cell-empty">{loading ? "Memuat…" : "Belum ada tiket gangguan"}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="new">
        <form onSubmit={create} className="max-w-2xl space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="ticket-form">
          <h3 className="font-display text-lg font-semibold">Tiket Gangguan Baru</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Pelanggan</Label>
              <select
                className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="ticket-customer"
                value={form.customer_id}
                onChange={(e) => {
                  const c = customers.find((x) => x.id === e.target.value);
                  setForm({ ...form, customer_id: e.target.value, customer_name: c ? c.name : "", phone: c ? c.phone || "" : "" });
                }}
              >
                <option value="">— isi manual —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.secret_name})</option>)}
              </select>
            </div>
            <Field label="Nama Pelapor" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="ticket-name" />
            <Field label="No. WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ticket-phone" />
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Kategori</Label>
              <select className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="ticket-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Prioritas</Label>
              <select className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="ticket-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Teknisi</Label>
              <select className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="ticket-assignee" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}>
                <option value="">— belum ditugaskan —</option>
                {teknisi.map((t) => <option key={t.id} value={t.id}>{t.username}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Deskripsi Gangguan</Label>
            <textarea
              className="min-h-[110px] w-full rounded-xl border border-input bg-secondary/50 p-3 text-sm" data-testid="ticket-description"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <Button type="submit" disabled={busy} data-testid="ticket-submit" className="rounded-full bg-gradient-to-r from-sky-500 to-violet-600 text-white">
            <Plus className="mr-1 h-4 w-4" /> Buat Tiket
          </Button>
        </form>
      </TabsContent>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="ticket-detail">
          <DialogHeader><DialogTitle className="font-mono">{detail?.code} · {detail?.customer_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <p>Kategori: <b>{detail.category}</b></p>
                <p>Prioritas: <b>{detail.priority}</b></p>
                <p>Status: <b data-testid="detail-status">{detail.status}</b></p>
                <p>Dibuat: {dt(detail.created_at)}</p>
              </div>
              <p className="rounded-xl bg-secondary/40 p-3 text-xs">{detail.description || "Tanpa deskripsi"}</p>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Teknisi</Label>
                <select className="h-10 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="detail-assignee" value={detail.assigned_to || ""} onChange={(e) => assign(detail, e.target.value)}>
                  <option value="">— belum ditugaskan —</option>
                  {teknisi.map((t) => <option key={t.id} value={t.id}>{t.username}</option>)}
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                {["buka", "proses", "selesai", "batal"].map((s) => (
                  <Button key={s} size="sm" variant={detail.status === s ? "default" : "outline"} className="rounded-full text-[11px]" data-testid={`detail-set-${s}`} onClick={() => setStatus(detail, s)}>
                    {s === "batal" ? <XCircle className="mr-1 h-3 w-3" /> : null}{s}
                  </Button>
                ))}
              </div>
              <div className="max-h-40 space-y-2 overflow-auto rounded-xl border border-border p-3 scrollbar-thin" data-testid="detail-timeline">
                {(detail.timeline || []).map((e) => (
                  <div key={`${e.at}-${e.action}`} className="text-xs">
                    <span className="text-muted-foreground">{dt(e.at)} · {e.by}</span> — <b>{e.action}</b>{e.note ? `: ${e.note}` : ""}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input className="h-10 rounded-xl bg-secondary/50" placeholder="Catatan penanganan…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="detail-note-input" />
                <Button size="sm" className="rounded-full" onClick={addNote} data-testid="detail-note-save">Simpan</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
